import React, { useCallback, useEffect, useState } from 'react';
import appointmentService from '../../services/appointmentService';
import { describeSlotError } from './SlotPicker';
import { addDaysIso, todayIso } from '../../utils/appointmentLogic';
import { downloadTextFile, reportToCsv } from '../../utils/appointmentExtras';

const Kpi = ({ label, value, hint }) => (
  <div className="apt-report-kpi">
    <span className="apt-report-kpi-value">{value}</span>
    <span className="apt-report-kpi-label">{label}</span>
    {hint && <span className="apt-muted">{hint}</span>}
  </div>
);

const Table = ({ title, rows, labelHeader }) => (
  <div className="apt-table-wrap" style={{ marginBottom: 16 }}>
    <table className="apt-table">
      <caption className="apt-caption">{title}</caption>
      <thead>
        <tr><th>{labelHeader}</th><th className="apt-right">Total</th><th className="apt-right">Registered</th><th className="apt-right">No-show</th><th className="apt-right">Cancelled</th><th className="apt-right">Open</th></tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={6} className="apt-empty">Nothing in this period.</td></tr>}
        {rows.map((r) => (
          <tr key={r.label}>
            <td>{r.label}</td>
            <td className="apt-right">{r.total}</td><td className="apt-right">{r.registered}</td>
            <td className="apt-right">{r.noShow}</td><td className="apt-right">{r.cancelled}</td><td className="apt-right">{r.open}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** Appointment figures for a period: outcomes, no-show rate, lead time, by department and by day, with a CSV download. */
export const AppointmentReportPanel = ({ centres, isAdmin, canExport }) => {
  const [from, setFrom] = useState(addDaysIso(todayIso(), -29));
  const [to, setTo] = useState(todayIso());
  const [centreId, setCentreId] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const run = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await appointmentService.report(from, to, centreId);
      if (res.success) setReport(res.data);
    } catch (err) {
      setReport(null);
      setError(describeSlotError(err));
    } finally {
      setLoading(false);
    }
  }, [from, to, centreId]);

  useEffect(() => { run(); /* first load; later runs are on demand */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invalid = !from || !to || to < from;

  return (
    <div className="apt-report">
      <div className="apt-toolbar">
        <div className="apt-range">
          <label className="apt-inline-label">From <input type="date" className="apt-input apt-input-sm" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} /></label>
          <label className="apt-inline-label">To <input type="date" className="apt-input apt-input-sm" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} /></label>
          {isAdmin && centres?.length > 0 && (
            <select className="apt-input apt-input-sm" aria-label="Centre" value={centreId} onChange={(e) => setCentreId(e.target.value)}>
              <option value="">All centres</option>
              {centres.map((c) => <option key={c.centreId} value={c.centreId}>{c.centreName}</option>)}
            </select>
          )}
          <button type="button" className="apt-btn apt-btn-primary apt-btn-sm" onClick={run} disabled={loading || invalid}>{loading ? 'Loading…' : 'Show report'}</button>
        </div>
        {canExport && report && (
          <button type="button" className="apt-btn apt-btn-sm" onClick={() => downloadTextFile(`appointment-report-${from}-to-${to}.csv`, reportToCsv(report))}>Download CSV</button>
        )}
      </div>
      {invalid && <p className="apt-error-text">Choose a valid period.</p>}
      {error && (
        <div className="apt-state apt-state-error" role="alert"><p>{error}</p><button type="button" className="apt-btn apt-btn-sm" onClick={run}>Try again</button></div>
      )}
      {report && (
        <>
          <div className="apt-report-kpis">
            <Kpi label="Appointments" value={report.total} />
            <Kpi label="Registered" value={report.byStatus.REGISTERED} />
            <Kpi label="No-show" value={report.byStatus.NO_SHOW} hint={`${report.noShowRatePercent}% of those decided`} />
            <Kpi label="Cancelled" value={report.byStatus.CANCELLED} />
            <Kpi label="Still open" value={(report.byStatus.BOOKED || 0) + (report.byStatus.CONFIRMED || 0)} />
            <Kpi label="Average lead time" value={`${report.averageLeadDays} days`} hint="booking to appointment" />
          </div>
          <Table title="By department" rows={report.byDepartment} labelHeader="Department" />
          <Table title="By day" rows={report.byDay} labelHeader="Date" />
        </>
      )}
    </div>
  );
};

export default AppointmentReportPanel;
